'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { FAQChatbot } from 'src/app/_components/faq-chatbot/FAQChatbot';
import LegacyArrowUpToLineIcon from 'src/app/_components/icons/legacy/LegacyArrowUpToLineIcon';
import LegacyShareIcon from 'src/app/_components/icons/legacy/LegacyShareIcon';
import { cn } from 'src/app/lib/utils/tailwindcss';
import { useCurrentPath } from 'src/hooks/useCurrentPath';
import { useAppDispatch } from 'src/store/client/config';
import { toastActions } from 'src/store/client/slices/toastSlice';
import utils from 'src/utils/utils';

import AdaptiveBackButton from './AdaptiveBackButton';

const FAB_MAPPER: {
  faq: {
    visiblePath: string[];
  };
  share: {
    visiblePath: string[];
  };
} = {
  faq: {
    visiblePath: [],
  },
  share: {
    visiblePath: [],
  },
};

export function FAB() {
  const currentPath = useCurrentPath();
  const pathname = usePathname();

  const dispatch = useAppDispatch();

  const [isVisible, setIsVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [footerHeight, setFooterHeight] = useState(0);

  // FAQ, 공유 버튼 숨김 여부 계산
  const isFaqVisible = FAB_MAPPER.faq.visiblePath.length > 0 && FAB_MAPPER.faq.visiblePath.includes(pathname ?? '');
  const isShareVisible =
    FAB_MAPPER.share.visiblePath.length > 0 && FAB_MAPPER.share.visiblePath.includes(pathname ?? '');

  // Footer 높이 갱신
  const updateFooterHeight = () => {
    const footer = document.getElementById('footer');
    setFooterHeight(footer ? footer.offsetHeight : 0);
  };

  // 스크롤 이벤트 핸들러
  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const footer = document.getElementById('footer');
    const _footerHeight = footer ? footer.offsetHeight : 0;

    setIsVisible(currentScrollY > 100);

    // Footer와 겹치는지 판단
    if (currentScrollY + windowHeight >= documentHeight - _footerHeight) {
      setFooterHeight(_footerHeight);
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  // Top으로 이동
  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 공유하기
  const handleShare = async () => {
    const isMobilePlatform = utils.isMobilePlatform();
    const urlToShare = `${process.env.NEXT_PUBLIC_URL}${currentPath}`;

    if (isMobilePlatform && navigator.share) {
      try {
        await navigator.share({
          title: '코튼시드(KottonSeed) - 디지털로 연결되는 팬덤 플랫폼',
          text: '코튼시드는 디지털 카드를 중심으로 팬과 브랜드, 아티스트를 연결하는 팬덤 플랫폼입니다.',
          url: urlToShare,
        });
      } catch (error) {
        console.error('Share failed:', error);
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(urlToShare);
        dispatch(
          toastActions.showToast({
            message: '링크가 복사되었습니다.',
          }),
        );
      } catch (error) {
        console.error('Copy failed:', error);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateFooterHeight);
    // 초기 footer 높이를 requestAnimationFrame으로 측정
    const rafId = requestAnimationFrame(() => {
      const footer = document.getElementById('footer');
      if (footer) {
        setFooterHeight(footer.offsetHeight);
      }
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateFooterHeight);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      className={cn(
        'fixed z-50',
        'flex flex-col gap-4',
        'right-5 mobile:right-[calc(50%-220px)]',
        'transition-opacity duration-300',
        isVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        isSticky && '!absolute',
      )}
      style={isSticky ? { bottom: `${footerHeight + 20}px` } : { bottom: 20 }}
    >
      {/* FAQ 버튼 조건부 렌더링 */}
      {isFaqVisible && (
        <button
          type="button"
          aria-label="FAQ"
          className={cn(
            'flex items-center justify-center',
            'h-[46px] w-[46px] rounded-full',
            'bg-urbangreen-brand-01 text-[15px] leading-[130%] font-bold tracking-[-0.16px] text-white',
            'shadow-[10px_10px_30px_0px_rgba(0,0,0,0.25)]',
            'cursor-pointer border-none',
            'hover:bg-[#23242a]',
            'focus:outline-none',
          )}
        >
          FAQ
        </button>
      )}
      {/* 공유 버튼 조건부 렌더링 */}
      {isShareVisible && (
        <button
          type="button"
          aria-label="공유하기"
          onClick={handleShare}
          className={cn(
            'flex items-center justify-center',
            'h-[46px] w-[46px] rounded-full',
            'shadow-[10px_10px_30px_0px_rgba(0,0,0,0.25)]',
            'cursor-pointer border-none',
            'focus:outline-none',
          )}
        >
          <LegacyShareIcon size={46} background="#f0f0f0" foreground="#686868" />
        </button>
      )}
      <FAQChatbot />
      <button
        type="button"
        aria-label="맨 위로 이동"
        onClick={handleScrollTop}
        className={cn(
          'flex items-center justify-center',
          'h-[46px] w-[46px] rounded-full',
          'bg-g-20',
          'shadow-[10px_10px_30px_0px_rgba(18,18,18,0.2)]',
          'cursor-pointer border-none transition-colors hover:bg-g-30',
          'focus:outline-none',
        )}
      >
        <LegacyArrowUpToLineIcon size={20} foreground="#686868" />
      </button>
      <AdaptiveBackButton />
    </div>
  );
}

export default FAB;
